import { useState, useMemo } from 'react';
import { Search, Filter, RefreshCw, ChevronLeft, ChevronRight, BarChart3, ArrowUpDown, Star } from 'lucide-react';
import SignalCard from './SignalCard';
import EmptyState from './EmptyState';
import { useData } from '../context/DataContext';
import { CombinedSignal, Stock } from '../data/mockData';

export default function SignalHall() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSignalType, setSelectedSignalType] = useState<string>('all');
  const [selectedStar, setSelectedStar] = useState<number>(0); // 0 = all
  const [sortOption, setSortOption] = useState<string>('changeDesc'); // changeDesc, changeAsc, countDesc, starsDesc
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const { signals, isLoading, refreshData } = useData();

  // Helper to calculate stars
  const calculateStars = (stock: Stock, signalTypes: string[]): number => {
    const signalCount = signalTypes.length;
    const changePercent = stock.changePercent || 0;
    
    // Count RPS > 90
    let rpsCount = 0;
    if ((stock.rps10 || 0) > 90) rpsCount++;
    if ((stock.rps20 || 0) > 90) rpsCount++;
    if ((stock.rps50 || 0) > 90) rpsCount++;
    if ((stock.rps120 || 0) > 90) rpsCount++;
    if ((stock.rps250 || 0) > 90) rpsCount++;

    // Rule 1: 3+ signals -> 5 stars
    if (signalCount >= 3) return 5;

    // Rule 2: 2 signals + 9.9%+ -> 5 stars
    if (signalCount >= 2 && changePercent >= 9.9) return 5;

    // Rule 3: weighted score
    const isLimitUp = changePercent >= 9.9 ? 1 : 0;
    const score = signalCount + rpsCount + isLimitUp;
    
    if (score >= 5) return 5;
    if (score >= 4) return 4;
    if (score >= 3) return 3;
    if (score >= 2) return 2;
    return 1;
  };

  // Aggregate signals by stock code
  const combinedSignals = useMemo(() => {
    const map = new Map<string, CombinedSignal>();
    
    signals.forEach(s => {
      if (map.has(s.stock.code)) {
        const existing = map.get(s.stock.code)!;
        if (!existing.signalTypes.includes(s.signalType)) {
          existing.signalTypes.push(s.signalType);
        }
      } else {
        map.set(s.stock.code, {
          id: s.stock.code,
          stock: s.stock,
          signalTypes: [s.signalType],
          signalStrength: 1 // Placeholder, will calculate below
        });
      }
    });

    // Calculate stars for each combined signal
    const result = Array.from(map.values()).map(item => {
      item.signalStrength = calculateStars(item.stock, item.signalTypes);
      return item;
    });
    
    return result;
  }, [signals]);

  // Extract unique signal types with counts
  const signalTypeStats = useMemo(() => {
    const stats = new Map<string, number>();
    combinedSignals.forEach(s => {
      s.signalTypes.forEach(type => {
        stats.set(type, (stats.get(type) || 0) + 1);
      });
    });
    
    const types = Array.from(new Set(signals.map(s => s.signalType)));
    return types.map(type => ({
      type,
      count: stats.get(type) || 0
    }));
  }, [signals, combinedSignals]);

  // Top 5 Industries
  const topIndustries = useMemo(() => {
    const industryCounts = new Map<string, number>();
    combinedSignals.forEach(s => {
      if (s.stock.topic) {
        industryCounts.set(s.stock.topic, (industryCounts.get(s.stock.topic) || 0) + 1);
      }
    });
    
    return Array.from(industryCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [combinedSignals]);

  const filteredSignals = useMemo(() => {
    let filtered = combinedSignals.filter(signal => {
      const matchesSearch = searchQuery === '' || 
        signal.stock.code.includes(searchQuery) || 
        signal.stock.name.includes(searchQuery);
      
      const matchesType = selectedSignalType === 'all' || 
        signal.signalTypes.includes(selectedSignalType);
      
      const matchesStar = selectedStar === 0 || 
        signal.signalStrength === selectedStar;

      return matchesSearch && matchesType && matchesStar;
    });

    // Sort logic
    return filtered.sort((a, b) => {
      switch (sortOption) {
        case 'changeDesc':
          return (b.stock.changePercent || 0) - (a.stock.changePercent || 0);
        case 'changeAsc':
          return (a.stock.changePercent || 0) - (b.stock.changePercent || 0);
        case 'starsDesc':
          if (b.signalStrength !== a.signalStrength) {
            return b.signalStrength - a.signalStrength;
          }
          return (b.stock.changePercent || 0) - (a.stock.changePercent || 0);
        case 'countDesc': // Signal count desc
           if (b.signalTypes.length !== a.signalTypes.length) {
             return b.signalTypes.length - a.signalTypes.length;
           }
           return (b.stock.changePercent || 0) - (a.stock.changePercent || 0);
        default:
          return (b.stock.changePercent || 0) - (a.stock.changePercent || 0);
      }
    });
  }, [combinedSignals, searchQuery, selectedSignalType, selectedStar, sortOption]);

  // Pagination logic
  const totalPages = Math.ceil(filteredSignals.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentSignals = filteredSignals.slice(startIndex, startIndex + pageSize);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    setIsRefreshing(false);
  };

  // Reset page when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedSignalType, selectedStar, sortOption]);

  if (isLoading && signals.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="flex flex-col items-center gap-2">
          <div className="animate-spin w-6 h-6 border-2 border-[#1a56db] border-t-transparent rounded-full" />
          <span>加载数据中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border px-4 py-3 space-y-3">
        <div className="flex justify-between items-center">
          <h1 className="text-foreground font-semibold text-lg">信号厅</h1>
          <button
            onClick={handleRefresh}
            className="p-1.5 rounded-md bg-muted/50 border border-border hover:bg-muted transition-colors text-foreground"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Top Stats */}
        {topIndustries.length > 0 && (
          <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar text-xs text-muted-foreground">
             <BarChart3 className="w-3 h-3 flex-shrink-0" />
             <span className="font-medium">热门行业:</span>
             {topIndustries.map(([industry, count], i) => (
               <span key={industry} className="whitespace-nowrap bg-muted/30 px-1.5 py-0.5 rounded">
                 {industry}({count})
               </span>
             ))}
          </div>
        )}
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="搜索股票代码或名称"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-muted/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a56db] focus:border-transparent text-foreground placeholder:text-muted-foreground text-sm"
          />
        </div>

        {/* Filters Row 1: Sort & Star */}
        <div className="flex gap-2">
           {/* Sort Dropdown */}
           <div className="relative min-w-[100px]">
             <select 
               value={sortOption}
               onChange={(e) => setSortOption(e.target.value)}
               className="w-full appearance-none bg-muted/50 border border-border text-foreground text-sm rounded-md pl-8 pr-8 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#1a56db]"
             >
               <option value="changeDesc">涨幅优先</option>
               <option value="changeAsc">跌幅优先</option>
               <option value="starsDesc">星级优先</option>
               <option value="countDesc">信号数量</option>
             </select>
             <ArrowUpDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
           </div>

           {/* Star Filter */}
           <div className="relative min-w-[100px]">
             <select 
               value={selectedStar}
               onChange={(e) => setSelectedStar(Number(e.target.value))}
               className="w-full appearance-none bg-muted/50 border border-border text-foreground text-sm rounded-md pl-8 pr-4 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#1a56db]"
             >
               <option value={0}>全部星级</option>
               <option value={5}>5星</option>
               <option value={4}>4星</option>
               <option value={3}>3星</option>
               <option value={2}>2星</option>
               <option value={1}>1星</option>
             </select>
             <Star className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
           </div>
        </div>

        {/* Filters Row 2: Signal Types */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
          <button
            onClick={() => setSelectedSignalType('all')}
            className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-colors border ${
              selectedSignalType === 'all'
                ? 'bg-[#1a56db] text-white border-[#1a56db]'
                : 'bg-background text-muted-foreground border-border hover:bg-muted'
            }`}
          >
            全部({combinedSignals.length})
          </button>
          {signalTypeStats.map(({ type, count }) => (
            <button
              key={type}
              onClick={() => setSelectedSignalType(type)}
              className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-colors border ${
                selectedSignalType === type
                  ? 'bg-[#1a56db] text-white border-[#1a56db]'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              {type}({count})
            </button>
          ))}
        </div>
      </div>

      {/* Signal Cards */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-background">
        {filteredSignals.length === 0 ? (
          <EmptyState 
            icon={Filter}
            title="暂无符合条件的信号"
            description="尝试调整筛选条件或搜索其他股票"
          />
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {currentSignals.map((signal) => (
              <SignalCard key={signal.id} signal={signal} />
            ))}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-background">
          <div className="text-sm text-muted-foreground">
            第 {currentPage} / {totalPages} 页
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1 rounded-md hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
